import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiResponse, OrganizationContactResponse, CertWatchRequest, ContactSearchResponse } from "../../types/certWatch";
import { approveOrDenyCertWatch, createCertConnect, editConnection, getCertWatchByContactId, getCertWatchByOrgId, removeCertWatchRequest, searchContactbyCCOId, setFilterAndFetch } from "../thunks/certConnectThunks";
import { t } from "i18next";
import { CertWatchResponse } from "../../types/allCertwatch";

interface CertConnectState {
  connectionList: OrganizationContactResponse[];
  connection: OrganizationContactResponse;
  searchContact: ContactSearchResponse;
  totalCount: number;
  requestId: string;
  page: number;
  pageSize: number;
  loading: boolean;
  editLoading: boolean;
  searchLoading: boolean;
searchError: string | { key: string; params?: Record<string, any> } | null;
  requestLoading: boolean;
  requestError: string | null;
  error: string | null;
  filters: Omit<CertWatchRequest, "page" | "pageSize">;
// New field for CertWatch
 certWatchData: CertWatchResponse[]; 
   success: boolean;
  message: string | null;
}

const initialState: CertConnectState = {
  connectionList: [],
  connection:null,
  searchContact: null,
  searchLoading: false,
  searchError: null,
  requestLoading: false,
  requestError: null,
  requestId: null,
  totalCount: 0,
  page: 1,
  pageSize: 25,
  loading: true,
  editLoading: false,
  error: null,
  filters: {
    Id:"",
    Designations: null,
    Availability: true,
    FilterMode: 0,
    SearchTerm: null,
    PageSize: 10,
    PageNumber: 1,
    SortBy:0,
    OrderBy:0
  },
  certWatchData: [], // initialize empty array
   success: false,
  message: null,
};

const mapErrorMessage = (rawMessage: string): string | { key: string; params?: Record<string, any> } => {
  if (rawMessage.includes("does not have an approved photo")) {
    const name = rawMessage.split(" does not have")[0];
  return { key: "contact.errors.noPhoto", params: { name } };
  }

  if (rawMessage.includes("has not completed their profile")) {
    const name = rawMessage.split(" has not")[0];
    return t("contact.errors.incompleteProfile", { name });
  }

  if (rawMessage.includes("account is locked")) {
    const name = rawMessage.split(" account is")[0];
    return t("contact.errors.lockedAccount", { name });
  }

  if (rawMessage.includes("Invalid CCO ID provided")) return t("contact.errors.invalidCcoId");
  if (rawMessage.includes("Contact not found")) return t("contact.errors.notFound");
  if (rawMessage.includes("already exists for candidate")) return t("contact.errors.duplicateCertRequest");

  return t("contact.errors.genericError");
};


const certConnectSlice = createSlice({
  name: "certConnect",
  initialState,
  reducers: {
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
      state.filters.PageNumber=action.payload;
    },
    setOrderBy(state, action: PayloadAction<number>) {
      state.filters.OrderBy = action.payload;
    },
    setSortBy(state, action: PayloadAction<number>) {
      state.filters.SortBy = action.payload;
    },
    setConnection(state, action: PayloadAction<OrganizationContactResponse>) {
      state.connection = action.payload;
    },
   setSearchContact(state, action: PayloadAction<ContactSearchResponse>) {
      state.searchContact = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.filters.PageSize=action.payload;
    },
    setFilter(state, action: PayloadAction<Partial<CertWatchRequest>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    reset(state) {
      state.connectionList = [];
      state.totalCount = 0;
      state.page = 1;
      state.pageSize = 10;
      state.loading = false;
      state.error = null;
      state.filters = {
        Id:"",
        Designations: null,
        Availability: true,
        FilterMode: 0,
        SearchTerm: null,
        PageNumber: 1,
        PageSize: 10,
        SortBy:0,
        OrderBy:0
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCertWatchByOrgId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getCertWatchByOrgId.fulfilled,
        (state, action: PayloadAction<ApiResponse<OrganizationContactResponse>>) => {
          state.loading = false;
          state.connectionList = action.payload.Data.Items;
          state.totalCount = action.payload.Data.TotalCount;
          state.page = action.payload.Data.PageNumber;
          state.pageSize = action.payload.Data.PageSize;
        }
      )
      .addCase(getCertWatchByOrgId.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch cert watch data";
      })
      // ✅ handle setFilterAndFetch
      .addCase(setFilterAndFetch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        setFilterAndFetch.fulfilled,
        (state, action: PayloadAction<ApiResponse<OrganizationContactResponse>>) => {
          state.loading = false;
          state.connectionList = action.payload.Data.Items;
          state.totalCount = action.payload.Data.TotalCount;
          state.page = action.payload.Data.PageNumber;
          state.pageSize = action.payload.Data.PageSize;
        }
      )
      .addCase(setFilterAndFetch.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch cert watch data";
      })

      .addCase(removeCertWatchRequest.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeCertWatchRequest.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      // optionally: remove the item from Items[] locally
      state.connectionList = state.connectionList.filter(
        (item) => item.CertWatchRequestId !== action.meta.arg.Id
      );
    })
    .addCase(removeCertWatchRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to remove cert watch request";
    })
    .addCase(editConnection.pending, (state) => {
      state.editLoading = true;
      state.error = null;
    })
.addCase(editConnection.fulfilled, (state, action: PayloadAction<OrganizationContactResponse>) => {
  state.editLoading = false;
  state.error = null;

  // update current connection directly
  state.connection = action.payload;
  // update list
  const index = state.connectionList.findIndex(
    (item) => item.CertWatchRequestId === action.payload.CertWatchRequestId
  );

  if (index !== -1) {
    state.connectionList[index] = action.payload;
  }
})

.addCase(editConnection.rejected, (state, action) => {
  state.editLoading = false;
  state.error = action.payload || "Failed to update connection";
})


.addCase(searchContactbyCCOId.pending, (state) => {
  state.searchLoading = true;
  state.searchError = null;
})
.addCase(searchContactbyCCOId.fulfilled, (state, action) => {
  state.searchLoading = false; // ✅ not `loading`
  state.searchContact = action.payload;
})
.addCase(searchContactbyCCOId.rejected, (state, action) => {
  state.searchLoading = false;
  const rawError = action.payload as string;

  state.searchError = mapErrorMessage(rawError);
})



.addCase(createCertConnect.pending, (state) => {
  state.requestLoading = true;
  state.requestError = null;
})
.addCase(createCertConnect.fulfilled, (state, action) => {
  state.requestLoading = false; // ✅ not `loading`
  state.requestId = action.payload;
})
.addCase(createCertConnect.rejected, (state, action) => {
  state.requestLoading = false; // ✅ not `loading`
  state.requestError = action.payload as string;
})
  .addCase(getCertWatchByContactId.pending, (state) => {
  state.loading = true;
  state.error = null;
})
.addCase(getCertWatchByContactId.fulfilled, (state, action) => {
  state.loading = false;
  state.certWatchData = action.payload as any; // cast to match state type
})
.addCase(getCertWatchByContactId.rejected, (state, action) => {
  state.loading = false;
  state.error = action.payload || "Failed to fetch cert watch data";
  state.certWatchData = [];
})
.addCase(approveOrDenyCertWatch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      // .addCase(approveOrDenyCertWatch.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.success = action.payload.success;
      //   state.message = action.payload.message;
      //   if(action.payload.success){
      // state.certWatchData = state.certWatchData.filter(
      //   (item) => item.Id !== action.meta.arg.certWatchRequestId
      // );
      //   }
       
      // })
      .addCase(approveOrDenyCertWatch.fulfilled, (state, action) => {
  state.loading = false;
  state.success = action.payload.success;
  state.message = action.payload.message;

  if (action.payload.success) {
    const id = action.meta.arg.certWatchRequestId;
    const isApproved = action.meta.arg.isApproved;

    if (isApproved) {
      //  Update status instead of removing
      state.certWatchData = state.certWatchData.map(item =>
        item.Id === id ? { ...item, StatusReason: "Approved" } : item
      );
    } else {
      // Denied: remove from list
      state.certWatchData = state.certWatchData.filter(item => item.Id !== id);
    }
  }
})
      .addCase(approveOrDenyCertWatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      });


  },
});

export const { setPage, setPageSize, setFilter, reset,setConnection,setSortBy,setOrderBy,setSearchContact } = certConnectSlice.actions;
export default certConnectSlice.reducer;
